/// <amd-module name="WidgetCommon/DateUtils/DateUtils" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

/** Describes allowed sample intervals for date sampling. */
export enum DateSampleInterval {
    Days,
    Weeks,
    Months
}

export const enum DayOfWeek {
    /**
     * Indicates Sunday.
     */
    Sunday = 0,
    /**
     * Indicates Monday.
     */
    Monday = 1,
    /**
     * Indicates Tuesday.
     */
    Tuesday = 2,
    /**
     * Indicates Wednesday.
     */
    Wednesday = 3,
    /**
     * Indicates Thursday.
     */
    Thursday = 4,
    /**
     * Indicates Friday.
     */
    Friday = 5,
    /**
     * Indicates Saturday.
     */
    Saturday = 6
}

/**
 * Add days to a date. This is DST safe since it doesn't use getTime() for the adjustment.
 * This will not change the time stamp when the DST threshold is passed
 * @param date The date to add days to
 * @param days Number of days to add
 */
export function addDays(date: Date, days: number): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + days,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
}

/**
 * Add months to a date. This is DST safe since it doesn't use getTime() for the adjustment.
 * This will not change the time stamp when the DST threshold is passed
 * @param date The date to add months to
 * @param months Number of months to add
 */
export function addMonths(date: Date, months: number): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth() + months,
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
}

/**
 * Returns the number of days between the two dates. Note that any time component is ignored and the dates
 * can be supplied in any order
 *
 * @param startDate The first date
 * @param endDate The second date
 * @param exclusive If true then the result is exclusive of the second date (Mon->Fri==4).
 * Otherwise the date includes the later date (Mon->Fri==5)
 */
export function daysBetweenDates(startDate: Date, endDate: Date, exclusive: boolean = false): number {
    const day = 1000 * 60 * 60 * 24;
    startDate = new Date(startDate.getTime());
    startDate.setHours(0, 0, 0, 0);

    endDate = new Date(endDate.getTime());
    endDate.setHours(0, 0, 0, 0);

    return Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / day) + (exclusive ? 0 : 1);
}

/**
 * Returns a date string representing today in the account time zone.
 * The string is in the format yyyy-MM-dd (e.g. 2019-05-03).
 * Note: Consumers of this can get a performance gain by including the following data provider in their contribution.
 * ms.vss-dashboards-web.collection-time-zone-data-provider
 */
export async function fetchTodayInAccountTimeZone(pageContext: IVssPageContext): Promise<string> {
    const dataProviderContributionId = "ms.vss-dashboards-web.collection-time-zone-data-provider";
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const accountTimeZone = await contributionService.getDataAsync<{ utcOffset: number }>(dataProviderContributionId);
    if (accountTimeZone == null) {
        const dataProviderExceptions = contributionService.getDataProviderExceptions();
        const ex = dataProviderExceptions[dataProviderContributionId];
        const message = ["Failed to retrieve today in account time zone.", ex.message].join(" ");
        throw new Error(message);
    }
    return new Date(Date.now() + accountTimeZone.utcOffset).toISOString().substr(0, 10);
}

export function getEndOfWeekDate(date: Date, lastWeekday: DayOfWeek): Date {
    const diff = lastWeekday.valueOf() - date.getDay();
    const newDate = addDays(date, diff);
    if (newDate < date) {
        return addDays(newDate, 7);
    } else {
        return newDate;
    }
}

/**
 * Takes a given date and returns the date of the first day in that month the date is in.
 * The time for the returned date will be 00:00:00 for whatever time zone the user's browser is in.
 * @param date the base date we are calculating from
 */
export function getStartOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Takes a given date and returns the date of the last day in that month the date is in.
 * The time for the returned date will be 00:00:00 for whatever time zone the user's browser is in.
 * @param date the base date we are calculating from
 */
export function getEndOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 0, 0, 0, 0);
}

/**
 * Shift a date object to UTC timezone.
 * @param localDate The date object to shift
 */
export function shiftToUtc(localDate: Date): Date {
    return new Date(
        localDate.getUTCFullYear(),
        localDate.getUTCMonth(),
        localDate.getUTCDate(),
        localDate.getUTCHours(),
        localDate.getUTCMinutes(),
        localDate.getUTCSeconds(),
        localDate.getUTCMilliseconds()
    );
}

/**
 * Gets the date for the end of a given time period from a base date. i.e. get the date for the end of the current week or month.
 * If Date Interval is "day" the baseDate will be returned.
 * @param baseDate The date to base the calculation off of
 * @param dateInterval Unit of time to base calculations on
 * @param endOfWeekDay optional last weekday specification, default is Friday.
 */
export function getEndOfDateIntervalPeriod(baseDate: Date, dateInterval: DateSampleInterval, lastDayOfWeek: DayOfWeek = DayOfWeek.Friday) {
    switch (dateInterval) {
        case DateSampleInterval.Months:
            return getEndOfMonth(baseDate);
        case DateSampleInterval.Weeks:
            return getEndOfWeekDate(baseDate, lastDayOfWeek);
        case DateSampleInterval.Days:
            return baseDate;
        default:
            throw new Error(`An unknown date Interval argument was recieved: ${dateInterval}`);
    }
}

/**
 * Parses a date string in the format YYYY-MM-DD into a local time zone Date object
 * @param dateString date string in YYYY-MM-DD format.
 */
export function parseIsoDateStringInLocalTime(dateString: string): Date {
    const parts = dateString.substring(0, 10).split("-");
    // Date constructor expects Month to be 0 based
    return new Date(+parts[0], +parts[1] - 1, +parts[2]);
}

/*  Format a date into date format as required by Widget e.g. YYYY-MM-DD */
export function toLocalDateIsoString(date: Date): string {
    // This helps suppress the timezone from the date before we use toISOString , that way the inherent conversion by ISO String is timezone agnostic
    // Hence this preserves the date in the original local time zone
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().substring(0, 10);
}
